import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Episode } from "@/mocks/episodes";

interface HeroCarouselProps {
  episodes: Episode[];
}

export default function HeroCarousel({ episodes }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = episodes.length;

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  if (total === 0) {
    return (
      <section className="w-full h-[420px] md:h-[560px] flex items-center justify-center bg-background-100">
        <div className="text-center">
          <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-200 text-accent-400 text-2xl mb-4">
            <i className="ri-movie-2-line" />
          </span>
          <p className="font-heading text-lg text-foreground-800">暂无最新更新</p>
          <p className="text-sm text-foreground-500 mt-1">剧集动态正在整理中，敬请期待</p>
        </div>
      </section>
    );
  }

  const go = (next: number) => setIndex((next + total) % total);
  const current = episodes[index];

  return (
    <section
      className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {episodes.map((episode, i) => (
        <div
          key={episode.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={episode.still}
            alt={`美国式禁忌2 第${episode.number}集 ${episode.title} 剧照`}
            title={`美国式禁忌2 第${episode.number}集 ${episode.title}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />

      <div className="relative h-full max-w-[1200px] mx-auto px-4 md:px-8 flex flex-col justify-center">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-primary-600/90 text-background-50 text-xs font-medium mb-4">
            <i className="ri-fire-fill text-accent-300" />
            焦点剧集 · 第 {current.number} 集
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
            {current.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-3">
            {current.tagline}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情解析
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100/80 backdrop-blur border border-background-300 hover:border-accent-500 text-foreground-900 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              浏览全部剧集
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur border border-background-300 text-foreground-900 hover:text-accent-400 hover:border-accent-500 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur border border-background-300 text-foreground-900 hover:text-accent-400 hover:border-accent-500 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-8 left-4 md:left-8 flex items-center gap-2">
        {episodes.map((episode, i) => (
          <button
            key={episode.id}
            type="button"
            aria-label={`切换到第 ${episode.number} 集`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-400"
            }`}
          />
        ))}
      </div>
    </section>
  );
}